// @ts-strict-ignore
import * as React from 'react';
import Text from 'antd/lib/typography/Text';
import Descriptions from 'antd/lib/descriptions';
import Space from 'antd/lib/space';
import Divider from 'antd/lib/divider';
import Tooltip from 'antd/lib/tooltip';
import Button from 'antd/lib/button';
import { LoadingOutlined, PlusOutlined, SettingOutlined, WarningFilled } from '@ant-design/icons';
import { RunwayUserAction } from '../../helpers/aclHelpers';
import PermissionedActionButton from '../HOCs/PermissionedActionButton';
import { MISSING_REQUIRED_INTEGRATIONS_WARNING, stepIsMissingRequiredIntegrations } from '@/helpers/integrationHelpers';
import { StepArchetypeGroup, StepGraphStep } from '@/types/workflow';
import { displayTextForReleaseStepType } from '@/helpers/displayHelpers';
import { AppPlatform, AppStoreType } from '@/types';
import { antGrey, runwayColors } from '../styles/globalStyles';
import { DestinationCounter } from '../appHome/DestinationCounter';
import { Destination } from '@/types/destinations';

const MAX_GROUP_NAME_LENGTH = 50;
const GROUP_LABEL_WIDTH = 220;
const GROUP_LABEL_PADDING = 24;
const GROUP_LABEL_CONTENT_WIDTH = GROUP_LABEL_WIDTH - 2 * GROUP_LABEL_PADDING;
const GROUP_NAME_WIDTH = GROUP_LABEL_CONTENT_WIDTH - 24;

interface WorkflowStepsSectionProps {
    archetypeGroup: StepArchetypeGroup;
    onEditClicked: (stepConfigId: string) => void;
    platform: AppPlatform;
    appStoreType: AppStoreType;
    destinationInfoByDestinationStepConfigId: Record<string, Destination>;
    canEditSettings: boolean;
    isWorkflowsCIStepEnabled: boolean;
    platformLabelOverride?: string;
    platformIconOverride?: (size: number) => React.ReactNode;
    // The workflow management page groups sections itself, so it opts out of the trailing margin.
    marginBottom?: number;
    // Only the workflow management page offers adding a step; omitting this hides the row.
    onAddStep?: () => void;
    // Reordering swaps the settings cog for a drag handle, so the row action is pluggable.
    renderStepAction?: (step: StepGraphStep) => React.ReactNode;
    // Replaces the Add step row, which reordering swaps for a drop zone.
    addStepRowOverride?: React.ReactNode;
    // Keeps the Add step row in place but unusable, explaining why.
    addStepDisabledReason?: string;
    // Mutes the rows of a group that cannot be acted on.
    isDisabled?: boolean;
    // Reordering makes the whole row the drag source, so the page wraps it.
    renderStepRow?: (step: StepGraphStep, row: React.ReactNode) => React.ReactNode;
    // Wraps the label so the page can make the whole cell drag this group.
    renderGroupLabel?: (label: React.ReactNode) => React.ReactNode;
    // Strikes out the archetype name, matching how Settings > General shows a step that is not required.
    isGroupLabelMuted?: boolean;
    // Makes the group label editable; an empty name puts the step type name back.
    onRenameGroup?: (name: string) => void;
    // Holds the label while its new name is being saved.
    isRenamingGroup?: boolean;
}

export const WorkflowStepsSection: React.FC<WorkflowStepsSectionProps> = ({
    archetypeGroup,
    onEditClicked,
    platform,
    appStoreType,
    destinationInfoByDestinationStepConfigId,
    canEditSettings,
    isWorkflowsCIStepEnabled,
    platformLabelOverride,
    platformIconOverride,
    marginBottom = 24,
    onAddStep,
    renderStepAction,
    addStepRowOverride,
    addStepDisabledReason,
    isDisabled,
    renderStepRow,
    renderGroupLabel,
    isGroupLabelMuted,
    onRenameGroup,
    isRenamingGroup,
}) => {
    const stepArchetypeDisplayText = displayTextForReleaseStepType(
        archetypeGroup.archetype,
        platform,
        appStoreType,
        isWorkflowsCIStepEnabled,
    );
    const groupLabel = (
        <Space direction="vertical" size={0} style={{ maxWidth: GROUP_LABEL_CONTENT_WIDTH }}>
            <Text
                strong
                style={{
                    color: isGroupLabelMuted ? antGrey.grey6 : antGrey.grey8,
                    textDecoration: isGroupLabelMuted ? 'line-through' : undefined,
                }}
                editable={
                    onRenameGroup &&
                    !isRenamingGroup && {
                        text: archetypeGroup.name || stepArchetypeDisplayText,
                        maxLength: MAX_GROUP_NAME_LENGTH,
                        onChange: (name) => {
                            // Leaving the step type name in place is the same as having no custom name.
                            const nextName = name.trim() === stepArchetypeDisplayText ? '' : name.trim();
                            if (nextName !== archetypeGroup.name) onRenameGroup(nextName);
                        },
                    }
                }
            >
                <Tooltip title={archetypeGroup.name || stepArchetypeDisplayText}>
                    <span
                        style={{
                            display: 'inline-block',
                            maxWidth: GROUP_NAME_WIDTH,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            verticalAlign: 'bottom',
                        }}
                    >
                        {archetypeGroup.name || stepArchetypeDisplayText}
                    </span>
                </Tooltip>
                {isRenamingGroup && <LoadingOutlined style={{ marginLeft: 8, color: antGrey.grey6 }} />}
            </Text>
            {!!archetypeGroup.name && (
                <Text ellipsis style={{ color: antGrey.grey6, fontSize: 12 }}>
                    {stepArchetypeDisplayText}
                </Text>
            )}
        </Space>
    );

    return (
        <Descriptions
            size="middle"
            layout="horizontal"
            bordered
            column={1}
            style={{ marginBottom }}
            labelStyle={{ width: GROUP_LABEL_WIDTH }}
        >
            <Descriptions.Item
                labelStyle={{
                    alignContent: 'start',
                    padding: `18px ${GROUP_LABEL_PADDING}px`,
                    position: 'relative',
                }}
                label={renderGroupLabel ? renderGroupLabel(groupLabel) : groupLabel}
                contentStyle={{ padding: '2px 0' }}
            >
                <Space
                    direction="vertical"
                    size={0}
                    style={{ width: '100%' }}
                    split={<Divider style={{ margin: 0 }} />}
                >
                    {archetypeGroup.steps.map((step) => {
                        const destinations = step.destinationStepConfigIds
                            .map(
                                (destinationStepConfigId) =>
                                    destinationInfoByDestinationStepConfigId[destinationStepConfigId],
                            )
                            .filter((destination) => destination !== undefined);
                        const row = (
                            <div
                                key={step.stepConfigId}
                                style={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '10px 20px',
                                    // Holds the height of a row whose action button is a plain icon, so
                                    // swapping the cog for a drag handle or nothing does not resize it.
                                    minHeight: 32,
                                    opacity: isDisabled ? 0.5 : undefined,
                                }}
                            >
                                <Space size={16}>
                                    <DestinationCounter
                                        size={20}
                                        destinations={destinations}
                                        platformLabelOverride={platformLabelOverride}
                                        platformIconOverride={platformIconOverride}
                                    />
                                    <Text>{step.stepName}</Text>
                                </Space>
                                {renderStepAction ?
                                    renderStepAction(step)
                                :   canEditSettings && (
                                        <Space size={4}>
                                            {stepIsMissingRequiredIntegrations(step) && (
                                                <Tooltip title={MISSING_REQUIRED_INTEGRATIONS_WARNING}>
                                                    <WarningFilled
                                                        style={{ color: runwayColors.statusYellow, fontSize: 14 }}
                                                    />
                                                </Tooltip>
                                            )}
                                            <Button
                                                type="link"
                                                icon={<SettingOutlined />}
                                                onClick={() => onEditClicked(step.stepConfigId)}
                                            />
                                        </Space>
                                    )
                                }
                            </div>
                        );

                        return renderStepRow ? renderStepRow(step, row) : row;
                    })}
                    {addStepRowOverride ?
                        <div style={{ padding: '10px 20px' }}>{addStepRowOverride}</div>
                    :   onAddStep && (
                            <div style={{ padding: '10px 20px' }}>
                                <Tooltip title={addStepDisabledReason}>
                                    <PermissionedActionButton
                                        actionType={RunwayUserAction.UAUpdateAppSettings}
                                        type="link"
                                        size="small"
                                        icon={<PlusOutlined />}
                                        disabled={!!addStepDisabledReason}
                                        onClick={onAddStep}
                                        style={{ paddingLeft: 0, color: antGrey.grey6 }}
                                        className="add-workflow-step"
                                    >
                                        Add step
                                    </PermissionedActionButton>
                                </Tooltip>
                            </div>
                        )
                    }
                </Space>
            </Descriptions.Item>
        </Descriptions>
    );
};
